import React, { useCallback, useMemo, useState } from "react";
import { Layout, Typography, Card, Switch, Flex, Modal } from "antd";
import {
  KeyOutlined,
  LogoutOutlined,
  RightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useThemeContext } from "../../../context/ThemeContext";
import { useSettingSidebarContext } from "../../../context/SettingSidebarScreenContext";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import Tickets from "../../../pages/Settings/Tickets";
import MyNumbers from "../../../pages/Settings/MyNumbers";
import CallHandling from "../../../pages/Settings/CallHandling";
import Subscription from "../../../pages/Settings/subscription";
import SmsSettings from "../../../pages/Settings/SmsSettings";
import AppSettings from "../../../pages/Settings/AppSettings";
import Security from "../../../pages/Settings/Security";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import messageCacheDB from "../../../services/messageCacheDB";
import audioCacheDB from "../../../services/audioCacheDB";
import conversationCacheDB from "../../../services/conversationCacheDB";
import searchIndex from "../../../services/searchIndex";
import { messageGraph } from "../../../services/messageGraph";
import GradientButton from "../../../components/common/UI/GradientButton";
import SearchInput from "../../../components/common/UI/SearchInput";
import "./index.scss";
import AssignAddons from "../../../pages/Settings/AssignAddons";
import { storageService } from "../../../services/storageService";
import { destroyEasySupport } from "../../../services/easySupportService";
import { SettingOption, SettingsScreen } from "../../../types";
import { SETTINGS } from "../../../constants";
import { HomePhoneProduct } from "../../../types/homePhone";

const { Sider } = Layout;
const { Text } = Typography;

interface SettingSidebarProps {
  selectedHomePhoneProductId?: string;
  onHomePhoneProductSelect: (product: HomePhoneProduct) => void;
  onOpenMyDevice: () => void;
}

const SettingSidebar: React.FC<SettingSidebarProps> = ({
  selectedHomePhoneProductId,
  onHomePhoneProductSelect,
  onOpenMyDevice,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { theme, toggleTheme } = useThemeContext();
  const { settingsCurrentScreen, setSettingsCurrentScreen } =
    useSettingSidebarContext();

  const [searchValue, setSearchValue] = useState<string>("");
  const [logoutConfirmationModalOpen, setLogoutConfirmationModalOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isSupportAgent = storageService.getIsSupportAgent();
  const options = useMemo<SettingOption[]>(() => {
    const roleOptions = isSupportAgent
      ? SETTINGS.AGENT_OPTIONS
      : SETTINGS.USER_OPTIONS;
    const [first, ...rest] = SETTINGS.BASE_OPTIONS;
    return [first, ...roleOptions, ...rest];
  }, [isSupportAgent]);

  const filteredOptions = useMemo(
    () =>
      searchValue
        ? options.filter((option) =>
            option.name.toLowerCase().includes(searchValue.toLowerCase()),
          )
        : options,
    [options, searchValue],
  );

  const handleBackToMain = useCallback(() => {
    destroyEasySupport();
    setSettingsCurrentScreen("main");
  }, [setSettingsCurrentScreen]);

  const handleNextScreen = useCallback(
    (screen: SettingsScreen) => {
      if (screen === "my-device") {
        onOpenMyDevice();
        return;
      }
      if (screen === "logout") {
        setLogoutConfirmationModalOpen(true);
        return;
      }
      setSettingsCurrentScreen(screen);
    },
    [onOpenMyDevice, setSettingsCurrentScreen],
  );

  const handleLogout = useCallback(async () => {
    setLoading(true);
    const url = ENDPOINTS.LOGOUT;

    try {
      const response = await apiInstance.get(url);

      if (response.status === 200) {
        toast.success("Logged out successfully");
        dispatch({ type: "RESET_APP" });

        // Clear all IndexedDB caches and in-memory stores on logout
        messageCacheDB.destroyAll().catch(() => {});
        conversationCacheDB.destroyAll().catch(() => {});
        audioCacheDB.cleanup(0).catch(() => {}); // remove all entries
        searchIndex.destroyAll().catch(() => {});
        messageGraph.clear();

        storageService.clearSession();
        navigate("/auth/login");
      } else {
        toast.error("Logout failed");
      }
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message || "An error occurred while logging out");
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  const subScreens: Partial<Record<SettingsScreen, React.ReactNode>> = {
    "my-numbers": <MyNumbers handleBackToPrevious={handleBackToMain} />,
    "call-handling": <CallHandling handleBackToPrevious={handleBackToMain} />,
    subscription: (
      <Subscription
        handleBackToPrevious={handleBackToMain}
        selectedHomePhoneProductId={selectedHomePhoneProductId}
        onHomePhoneProductSelect={onHomePhoneProductSelect}
      />
    ),
    "assign-addons": <AssignAddons handleBackToPrevious={handleBackToMain} />,
    "sms-setting": <SmsSettings handleBackToPrevious={handleBackToMain} />,
    "app-settings": <AppSettings handleBackToPrevious={handleBackToMain} />,
    security: <Security handleBackToPrevious={handleBackToMain} />,
    tickets: <Tickets handleBackToPrevious={handleBackToMain} />,
  };

  const getIcon = useCallback(
    (iconName: string): React.ReactNode => {
      const img = (src: string, alt: string, size = 16) => (
        <img
          src={`/assets/icons/${theme}/${src}.png`}
          width={size}
          height={size}
          alt={alt}
        />
      );
      switch (iconName) {
        case "Number":
          return img("my-numbers", "my-numbers");
        case "laptop":
          return img("call-handling", "call-handling");
        case "schedule":
          return img("subscription", "subscription");
        case "SmsSetting":
          return img("sms", "sms", 18);
        case "Notificat":
          return img("notification", "notification");
        case "settings":
          return <SettingOutlined />;
        case "logout":
          return <LogoutOutlined />;
        case "ticket":
          return img("tickets", "tickets");
        case "dark":
          return img("dark-mode", "dark-mode");
        case "Vectorcalling":
          return (
            <img
              src="/assets/icons/Vectorcalling.svg"
              width={16}
              height={16}
              alt="Vectorcalling"
            />
          );
        default:
          return <KeyOutlined rotate={320} />;
      }
    },
    [theme],
  );

  return (
    <Sider className="settings-sider" width={450}>
      {settingsCurrentScreen !== "main" ? (
        (subScreens[settingsCurrentScreen as SettingsScreen] ?? null)
      ) : (
        <div className="settings-main-container">
          <Flex vertical gap={10}>
            <Text className="page-heading">Settings</Text>

            <SearchInput
              placeholder="Search..."
              value={searchValue}
              onChange={setSearchValue}
            />
          </Flex>

          <Flex vertical gap={10}>
            {filteredOptions.map((item) => (
              <Card
                key={item.id}
                onClick={() => handleNextScreen(item.screen)}
                className="option-card"
                bordered={false}
              >
                <Flex align="center" justify="space-between">
                  <Flex gap={10} align="center">
                    <GradientButton className="option-icon">
                      {getIcon(item.icon)}
                    </GradientButton>
                    <h6 className="text-primary option-title">{item.name}</h6>
                  </Flex>
                  <>
                    {item.id === "9" ? (
                      <div className="custom-switch">
                        <Switch
                          className="custom-switch"
                          checked={theme === "dark"}
                          onChange={() => toggleTheme()}
                        />
                      </div>
                    ) : (
                      <RightOutlined className="right-icon" />
                    )}
                  </>
                </Flex>
              </Card>
            ))}
          </Flex>
        </div>
      )}

      <Modal
        title="Confirm Logout"
        className="custom-modal"
        centered
        open={logoutConfirmationModalOpen}
        onOk={handleLogout}
        onCancel={() => setLogoutConfirmationModalOpen(false)}
        okText="Yes"
        cancelText="No"
        okButtonProps={{
          className: "modal-ok-btn",
          loading: loading,
        }}
        cancelButtonProps={{
          className: "modal-cancel-btn",
          disabled: loading,
          type: "text",
        }}
      >
        <p className="text-primary">Are you sure you want to Logout?</p>
      </Modal>
    </Sider>
  );
};

export default SettingSidebar;
